import { VideoMetadata, VideoTranscript, TranscriptItem } from './youtubeService';
import { OutputLanguage, ContentCategory } from './contentService';
import { generateWithAI, parseJsonFromAIResponse } from './multiModelService';
import axios, { AxiosError } from 'axios';

// Constants for Whisper API (still using OpenAI for transcription)
const API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const OPENAI_API_URL = 'https://api.openai.com/v1';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

export interface CapsuleContent {
  fullCapsule: string;
  keyInsights: string[];
  mindMap: MindMapNode;
  faqs: FAQ[];
  mcqs: MCQ[];
  flashCards: FlashCard[];
  studyNotes: string;
  terminology: TermDefinition[];
}

export interface MindMapNode {
  name: string;
  children?: MindMapNode[];
}

export interface FAQ {
  question: string;
  answer: string;
}

export interface MCQ {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export interface FlashCard {
  front: string;
  back: string;
}

export interface TermDefinition {
  term: string;
  definition: string;
  context?: string;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

/**
 * Helper function to implement retry logic for API calls
 * @param fn Function to retry
 * @param retries Number of retries
 * @param delay Delay between retries in ms
 * @returns Promise with the result of the function
 */
const withRetry = async <T>(
  fn: () => Promise<T>,
  retries: number = MAX_RETRIES,
  delay: number = RETRY_DELAY
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    
    // Check if error is rate limiting or server error
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 429 || axiosError.response?.status === 500) {
        console.log(`API call failed with status ${axiosError.response.status}. Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return withRetry(fn, retries - 1, delay * 2); // Exponential backoff
      }
    }
    
    throw error;
  }
};

/**
 * Transcribes audio using OpenAI's Whisper API
 * @param audioBlob Audio blob to transcribe
 * @returns Promise with transcription result
 */
export const transcribeAudio = async (audioBlob: Blob): Promise<VideoTranscript> => {
  return withRetry(async () => {
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.mp3');
      formData.append('model', 'whisper-1');
      formData.append('response_format', 'verbose_json');
      formData.append('timestamp_granularities', 'word');

      const response = await axios.post(
        `${OPENAI_API_URL}/audio/transcriptions`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${API_KEY}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Transform Whisper API response to our format
      const segments = response.data.segments || [];
      const items: TranscriptItem[] = segments.map((segment: any) => ({
        text: segment.text,
        start: segment.start,
        duration: segment.end - segment.start,
      }));

      return {
        items,
        language: response.data.language || 'en',
        isAutoGenerated: false,
      };
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  });
};

/**
 * Generates capsule content using GPT-4 Turbo
 * @param metadata Video metadata
 * @param transcript Video transcript
 * @param outputLanguage Desired output language
 * @param category Content category
 * @returns Promise with generated capsule content
 */
export const generateCapsuleContent = async (
  metadata: VideoMetadata,
  transcript: VideoTranscript,
  outputLanguage: string,
  category: string
): Promise<CapsuleContent> => {
  try {
    // Always use real API, never use mock data
    const useRealApi = true;
    
    if (useRealApi) {
      return withRetry(async () => {
        try {
          // Combine transcript items into a single text
          const transcriptText = transcript.items.map(item => item.text).join(' ');
          
          // Create system prompt based on category and language
          const systemPrompt = createSystemPrompt(category, outputLanguage);
          
          // Create user prompt with video metadata and transcript
          const userPrompt = `
            Video Title: ${metadata.title}
            Channel: ${metadata.channelTitle}
            Published: ${metadata.publishedAt}
            Duration: ${metadata.duration}
            
            Description:
            ${metadata.description}
            
            Transcript:
            ${transcriptText}
          `;
          
          // Process all videos consistently without special casing
          
          // Use the multiModelService to generate content with the currently enabled AI model
          const aiResponse = await generateWithAI(systemPrompt, userPrompt);
          
          // Parse the response
          const content = parseJsonFromAIResponse(aiResponse);
          
          return {
            fullCapsule: content.fullCapsule || '',
            keyInsights: content.keyInsights || [],
            mindMap: content.mindMap || { name: metadata.title, children: [] },
            faqs: content.faqs || [],
            mcqs: content.mcqs || [],
            flashCards: content.flashCards || [],
            studyNotes: content.studyNotes || '',
            terminology: content.terminology || [],
          };
        } catch (error) {
          console.error('Error generating capsule content:', error);
          
          // Enhanced error handling with more specific error messages
          if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            
            if (axiosError.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              console.error('API response error:', axiosError.response.status, axiosError.response.data);
              
              if (axiosError.response.status === 401) {
                throw new Error('Authentication error: Invalid API key or unauthorized access');
              } else if (axiosError.response.status === 429) {
                throw new Error('Rate limit exceeded: Too many requests to the API');
              } else if (axiosError.response.status >= 500) {
                throw new Error('Server error: Please try again later');
              }
            } else if (axiosError.request) {
              // The request was made but no response was received
              console.error('No response received:', axiosError.request);
              throw new Error('Network error: No response received from API');
            } else {
              // Something happened in setting up the request that triggered an Error
              console.error('Request setup error:', axiosError.message);
              throw new Error(`Request setup error: ${axiosError.message}`);
            }
          }
          
          // For non-Axios errors
          throw new Error(`Error generating content: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      });
    } else {
      // Use mock data for development
      console.log(`Generating mock capsule content for: ${metadata.title}`);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate mock content based on video title
      let title = metadata.title;
      if (title === 'Sample YouTube Video' && metadata.id === 'dQw4w9WgXcQ') {
        title = 'Rick Astley - Never Gonna Give You Up (Official Music Video)';
      }
      
      // Adjust language based on outputLanguage
      let languagePrefix = '';
      switch (outputLanguage) {
        case OutputLanguage.Hindi:
          languagePrefix = 'हिंदी: ';
          break;
        case OutputLanguage.Hinglish:
          languagePrefix = 'Hinglish: ';
          break;
        case OutputLanguage.Brazilian:
          languagePrefix = 'Português: ';
          break;
        case OutputLanguage.Brazinglish:
          languagePrefix = 'Brazinglish: ';
          break;
        case OutputLanguage.Marathi:
          languagePrefix = 'मराठी: ';
          break;
        case OutputLanguage.Marathinglish:
          languagePrefix = 'Marathinglish: ';
          break;
        default:
          languagePrefix = '';
      }
      
      // Return mock data
      return {
        fullCapsule: `# ${title}

## Introduction
${languagePrefix}This video by ${metadata.channelTitle} explores the concept of using affirmations to align your fate in your favor. The presenter emphasizes that repeating specific affirmations 100 times can have a profound impact on your subconscious mind and ultimately your reality.

## Main Concept
The core premise of this video is that our thoughts create our reality, and by consciously directing our thoughts through repeated affirmations, we can influence our life circumstances. The presenter explains that the number 100 is significant because it represents the threshold needed to bypass the conscious mind's resistance and implant new beliefs directly into the subconscious.

## The Science Behind Affirmations
The video discusses how neural pathways in our brain are strengthened through repetition. When we repeat an affirmation multiple times, we create new neural connections that support that belief. The presenter cites research showing that it takes approximately 21 days to form a new habit, but 100 repetitions in a single session can accelerate this process by creating a strong initial imprint.

## The Recommended Affirmation
The specific affirmation recommended in the video is: "I am aligned with the highest vibration of abundance and success. Everything I desire comes to me effortlessly." The presenter suggests saying this with feeling and conviction, as emotional intensity enhances the effectiveness of the practice.

## Implementation Strategy
The video outlines a specific protocol for maximum effectiveness:
1. Find a quiet place where you won't be disturbed
2. Sit comfortably with your back straight
3. Take 10 deep breaths to center yourself
4. Say the affirmation out loud 100 times without interruption
5. As you speak, visualize your desires as already fulfilled
6. End the session with gratitude

## Expected Results
According to the presenter, consistent practice of this technique should yield noticeable results within 30 days. These may include:
- Unexpected opportunities appearing in your life
- Meeting helpful people at just the right time
- Intuitive insights leading to better decisions
- A general sense of flow and ease in daily activities

## Common Obstacles and Solutions
The video addresses several common challenges people face:
- Skepticism: The presenter suggests trying it as an experiment rather than needing to believe it first
- Inconsistency: Recommends setting a specific time each day for the practice
- Lack of results: Patience is emphasized, along with examining if you're practicing with genuine feeling

## Scientific Perspective
While acknowledging that some viewers may be skeptical, the presenter references studies in neuroplasticity and quantum physics that suggest our thoughts can influence our experience of reality. The video strikes a balance between spiritual concepts and scientific principles.

## Conclusion
The video concludes by encouraging viewers to commit to this practice for at least 30 days before judging its effectiveness. The presenter shares testimonials from others who have successfully used this technique to transform various aspects of their lives including finances, relationships, and health.`,
        keyInsights: [
          `${languagePrefix}Repeating affirmations 100 times can bypass the conscious mind's resistance and implant new beliefs directly into the subconscious.`,
          `${languagePrefix}The specific affirmation recommended is: "I am aligned with the highest vibration of abundance and success. Everything I desire comes to me effortlessly."`,
          `${languagePrefix}Neural pathways in our brain are strengthened through repetition, creating new connections that support the beliefs we're affirming.`,
          `${languagePrefix}The practice should be done with emotional intensity and visualization for maximum effectiveness.`,
          `${languagePrefix}A specific 6-step protocol is recommended: find a quiet place, sit comfortably, take 10 deep breaths, say the affirmation 100 times, visualize desires as fulfilled, and end with gratitude.`,
          `${languagePrefix}Consistent practice should yield noticeable results within 30 days, including unexpected opportunities and better intuitive decision-making.`,
          `${languagePrefix}The video addresses common obstacles like skepticism and inconsistency with practical solutions.`,
          `${languagePrefix}Studies in neuroplasticity and quantum physics are referenced to support the effectiveness of this practice.`
        ],
        mindMap: {
          name: title,
          children: [
            {
              name: 'Fundamental Principles',
              children: [
                { name: 'Core Concept 1', children: [] },
                { name: 'Core Concept 2', children: [] },
                { name: 'Core Concept 3', children: [] }
              ]
            },
            {
              name: 'Practical Applications',
              children: [
                { name: 'Application 1', children: [] },
                { name: 'Application 2', children: [] },
                { name: 'Application 3', children: [] }
              ]
            },
            {
              name: 'Common Misconceptions',
              children: [
                { name: 'Misconception 1', children: [] },
                { name: 'Misconception 2', children: [] }
              ]
            }
          ]
        },
        faqs: [
          {
            question: `${languagePrefix}Why should I repeat an affirmation 100 times?`,
            answer: `${languagePrefix}According to the video, repeating an affirmation 100 times helps bypass the conscious mind's resistance and implant the belief directly into your subconscious. This number represents a threshold that creates a strong initial neural imprint, accelerating the process of belief change compared to the typical 21 days needed to form a new habit.`
          },
          {
            question: `${languagePrefix}What is the specific affirmation recommended in the video?`,
            answer: `${languagePrefix}The specific affirmation recommended is: "I am aligned with the highest vibration of abundance and success. Everything I desire comes to me effortlessly." The video emphasizes saying this with genuine feeling and conviction for maximum effectiveness.`
          },
          {
            question: `${languagePrefix}How should I practice this affirmation technique?`,
            answer: `${languagePrefix}The video outlines a 6-step protocol: 1) Find a quiet place where you won't be disturbed, 2) Sit comfortably with your back straight, 3) Take 10 deep breaths to center yourself, 4) Say the affirmation out loud 100 times without interruption, 5) As you speak, visualize your desires as already fulfilled, and 6) End the session with gratitude.`
          },
          {
            question: `${languagePrefix}How long before I see results from this practice?`,
            answer: `${languagePrefix}According to the presenter, consistent practice should yield noticeable results within 30 days. These may include unexpected opportunities appearing in your life, meeting helpful people at just the right time, intuitive insights leading to better decisions, and a general sense of flow and ease in daily activities.`
          },
          {
            question: `${languagePrefix}What if I'm skeptical about affirmations?`,
            answer: `${languagePrefix}The video addresses skepticism directly, suggesting that you try it as an experiment rather than needing to believe in it first. The presenter references studies in neuroplasticity and quantum physics to provide scientific backing, while acknowledging that some viewers may be skeptical. The recommendation is to commit to the practice for at least 30 days before judging its effectiveness.`
          },
          {
            question: `${languagePrefix}What are common obstacles to success with this technique?`,
            answer: `${languagePrefix}The video identifies three main obstacles: 1) Skepticism, which can be overcome by approaching it as an experiment, 2) Inconsistency, which can be addressed by setting a specific time each day for the practice, and 3) Lack of results, which requires patience and examining if you're practicing with genuine feeling and emotional intensity.`
          }
        ],
        mcqs: [
          {
            question: `${languagePrefix}What is the main focus of the video?`,
            options: [
              `${languagePrefix}Entertainment`,
              `${languagePrefix}Educational content`,
              `${languagePrefix}Product review`,
              `${languagePrefix}News report`
            ],
            correctAnswer: 1,
            explanation: `${languagePrefix}The video primarily focuses on educational content, explaining concepts and their applications.`
          },
          {
            question: `${languagePrefix}Which of the following is NOT a fundamental principle covered in the video?`,
            options: [
              `${languagePrefix}Core Concept 1`,
              `${languagePrefix}Core Concept 2`,
              `${languagePrefix}Core Concept 3`,
              `${languagePrefix}Advanced Theory X`
            ],
            correctAnswer: 3,
            explanation: `${languagePrefix}Advanced Theory X is not mentioned as one of the fundamental principles in the video.`
          },
          {
            question: `${languagePrefix}How many practical applications are demonstrated in the video?`,
            options: [
              `${languagePrefix}1`,
              `${languagePrefix}2`,
              `${languagePrefix}3`,
              `${languagePrefix}4`
            ],
            correctAnswer: 2,
            explanation: `${languagePrefix}The video demonstrates 3 practical applications of the concepts.`
          },
          {
            question: `${languagePrefix}What does the presenter do to address misconceptions?`,
            options: [
              `${languagePrefix}Ignores them completely`,
              `${languagePrefix}Briefly mentions them without explanation`,
              `${languagePrefix}Clarifies them with accurate information`,
              `${languagePrefix}Refers viewers to external resources`
            ],
            correctAnswer: 2,
            explanation: `${languagePrefix}The presenter clarifies misconceptions by providing accurate information to correct misunderstandings.`
          },
          {
            question: `${languagePrefix}Which of the following best describes the video's approach to the subject matter?`,
            options: [
              `${languagePrefix}Theoretical only`,
              `${languagePrefix}Practical only`,
              `${languagePrefix}Both theoretical and practical`,
              `${languagePrefix}Neither theoretical nor practical`
            ],
            correctAnswer: 2,
            explanation: `${languagePrefix}The video takes a balanced approach, covering both theoretical principles and practical applications.`
          }
        ],
        flashCards: [
          {
            front: `${languagePrefix}Core Concept 1`,
            back: `${languagePrefix}Explanation of Core Concept 1 and its importance to the overall topic.`
          },
          {
            front: `${languagePrefix}Core Concept 2`,
            back: `${languagePrefix}Explanation of Core Concept 2 and its relationship to other concepts.`
          },
          {
            front: `${languagePrefix}Core Concept 3`,
            back: `${languagePrefix}Explanation of Core Concept 3 and how it builds upon the previous concepts.`
          },
          {
            front: `${languagePrefix}Application 1`,
            back: `${languagePrefix}Description of Application 1 and how it demonstrates the practical use of the concepts.`
          },
          {
            front: `${languagePrefix}Application 2`,
            back: `${languagePrefix}Description of Application 2 and its real-world relevance.`
          },
          {
            front: `${languagePrefix}Application 3`,
            back: `${languagePrefix}Description of Application 3 and how it differs from the other applications.`
          },
          {
            front: `${languagePrefix}Misconception 1`,
            back: `${languagePrefix}Explanation of why Misconception 1 is incorrect and what the correct understanding should be.`
          },
          {
            front: `${languagePrefix}Misconception 2`,
            back: `${languagePrefix}Explanation of why Misconception 2 is incorrect and what the correct understanding should be.`
          }
        ],
        studyNotes: `${languagePrefix}# Study Notes: ${title}\n\n## Introduction\nThis video provides a comprehensive overview of the topic, covering fundamental principles, practical applications, and common misconceptions.\n\n## Fundamental Principles\n- Core Concept 1: Explanation and importance\n- Core Concept 2: Explanation and relationship to other concepts\n- Core Concept 3: Explanation and how it builds upon previous concepts\n\n## Practical Applications\n- Application 1: Description and demonstration of practical use\n- Application 2: Description and real-world relevance\n- Application 3: Description and comparison with other applications\n\n## Common Misconceptions\n- Misconception 1: Why it's incorrect and the correct understanding\n- Misconception 2: Why it's incorrect and the correct understanding\n\n## Summary\nThe video successfully explains the topic by combining theoretical knowledge with practical examples, making it accessible and useful for viewers.`,
        terminology: [
          {
            term: `${languagePrefix}Core Concept 1`,
            definition: `${languagePrefix}Definition of Core Concept 1`,
            context: `${languagePrefix}Used in the context of fundamental principles.`
          },
          {
            term: `${languagePrefix}Core Concept 2`,
            definition: `${languagePrefix}Definition of Core Concept 2`,
            context: `${languagePrefix}Used in the context of fundamental principles.`
          },
          {
            term: `${languagePrefix}Core Concept 3`,
            definition: `${languagePrefix}Definition of Core Concept 3`,
            context: `${languagePrefix}Used in the context of fundamental principles.`
          },
          {
            term: `${languagePrefix}Application 1`,
            definition: `${languagePrefix}Definition of Application 1`,
            context: `${languagePrefix}Used in the context of practical applications.`
          },
          {
            term: `${languagePrefix}Application 2`,
            definition: `${languagePrefix}Definition of Application 2`,
            context: `${languagePrefix}Used in the context of practical applications.`
          },
          {
            term: `${languagePrefix}Application 3`,
            definition: `${languagePrefix}Definition of Application 3`,
            context: `${languagePrefix}Used in the context of practical applications.`
          }
        ]
      };
    }
  } catch (error) {
    console.error('Error generating capsule content:', error);
    
    // Fallback to basic mock data in case of error
    return {
      fullCapsule: 'Error generating full capsule content. Please try again later.',
      keyInsights: ['Error generating key insights. Please try again later.'],
      mindMap: { name: 'Error', children: [] },
      faqs: [{ question: 'Error?', answer: 'Error generating FAQs. Please try again later.' }],
      mcqs: [{
        question: 'Error?',
        options: ['Error 1', 'Error 2', 'Error 3', 'Error 4'],
        correctAnswer: 0,
        explanation: 'Error generating MCQs. Please try again later.'
      }],
      flashCards: [{ front: 'Error', back: 'Error generating flash cards. Please try again later.' }],
      studyNotes: 'Error generating study notes. Please try again later.',
      terminology: [{ term: 'Error', definition: 'Error generating terminology. Please try again later.' }]
    };
  }
};

/**
 * Creates a system prompt based on category and language
 * @param category Content category
 * @param outputLanguage Desired output language
 * @returns System prompt for GPT-4
 */
const createSystemPrompt = (category: string, outputLanguage: string): string => {
  let languageInstruction = '';
  
  // Handle different language options using enum
  switch (outputLanguage) {
    case OutputLanguage.English:
      languageInstruction = 'Respond in fluent, natural English. Use clear and concise language that is easy to understand.';
      break;
    case OutputLanguage.Hindi:
      languageInstruction = 'हिंदी में उत्तर दें। स्पष्ट और सरल भाषा का प्रयोग करें जो समझने में आसान हो। सभी तकनीकी शब्दों को हिंदी में अनुवादित करें और यदि आवश्यक हो तो कोष्ठक में अंग्रेजी शब्द भी दें।';
      break;
    case OutputLanguage.Hinglish:
      languageInstruction = 'IMPORTANT: Respond in Hinglish (Hindi written in English script). Use Hindi vocabulary and grammar but write it using English letters. For example: "Is video mein business coach Shakil Kadva dhande aur company ke beech ke antar ke bare mein baat kar rahe hain." DO NOT respond in pure English or pure Hindi with Devanagari script. Your response MUST be in Hinglish (Hindi written in English script). Make sure to use Hindi sentence structure and vocabulary.';
      break;
    case OutputLanguage.Brazilian:
      languageInstruction = 'Responda em português brasileiro fluente e natural. Use linguagem clara e concisa que seja fácil de entender. Traduza todos os termos técnicos para o português e, se necessário, forneça o termo em inglês entre parênteses.';
      break;
    case OutputLanguage.Brazinglish:
      languageInstruction = 'Responda em português brasileiro misturado com inglês. Use frases e explicações em português, mas mantenha os termos técnicos em inglês. Use um estilo conversacional natural como se estivesse falando com um amigo.';
      break;
    case OutputLanguage.Marathi:
      languageInstruction = 'मराठीमध्ये उत्तर द्या. स्पष्ट आणि सोपी भाषा वापरा जी समजण्यास सोपी आहे. सर्व तांत्रिक शब्दांचे मराठीत भाषांतर करा आणि आवश्यक असल्यास कंसात इंग्रजी शब्द द्या.';
      break;
    case OutputLanguage.Marathinglish:
      languageInstruction = 'Marathinglish (Marathi mixed with English) मध्ये respond करा. Marathi sentences आणि explanations वापरा but technical terms इंग्रजीमध्ये ठेवा. Natural conversational style वापरा जसे की तुम्ही एखाद्या मित्राशी बोलत आहात.';
      break;
    default:
      languageInstruction = 'Respond in fluent, natural English. Use clear and concise language that is easy to understand.';
  }
  
  // Base prompt for all categories
  let basePrompt = `
    You are SharkPanzer, an advanced AI assistant that creates comprehensive knowledge capsules from YouTube video information.
    ${languageInstruction}
    
    Your task is to analyze the provided video metadata and any available transcript information, then create a complete knowledge capsule with the following components. IMPORTANT: You must work with whatever information is provided - even if the transcript is minimal or incomplete, use the video title, description, and other metadata to create meaningful content. NEVER ask for more information or complain about insufficient data.
    1. Full Capsule: Create a COMPREHENSIVE breakdown of the ENTIRE video content in NUMBERED POINTS (1, 2, 3, etc.). Include as many points as needed to cover ALL important details from the video. Each point should be clear, concise, and factually accurate.
    
    IMPORTANT: Use the video metadata and transcript I've provided to create this content. DO NOT ask for additional information or transcripts - work with what you have. If the transcript is limited, use the video title, description, and other metadata to create meaningful content.
    
    NEVER respond with phrases like "Sorry, but you haven't provided the actual transcript" or similar. Instead, create the best possible content with the information available.
    
    The goal is to make watching the video COMPLETELY UNNECESSARY after reading these points. Users should feel 100% confident they haven't missed ANYTHING from the original video.
    
    CRITICAL: Your response MUST contain enough numbered points to cover EVERY important detail from the video. The goal is to make watching the video COMPLETELY UNNECESSARY. For most videos, you should aim for at least 20-30 detailed points to ensure comprehensive coverage. Each point should be substantial and informative. If the video is longer or more complex, you might need 40-50+ points. Focus on quality AND quantity - we need COMPLETE coverage of the video content.
    
    DO NOT leave any empty points or placeholders. EVERY point must have actual content - not just quotes or empty text. DO NOT skip numbers or leave blank lines between points. DO NOT include points that are just quotes (") or empty text.
    
    EXTREMELY IMPORTANT: DO NOT just say "The speaker discussed X" or "The presenter talked about Y" - INSTEAD, you MUST provide the ACTUAL CONTENT and SPECIFIC DETAILS of what was said about X or Y. For example, instead of "Shakil Kadva talked about the difference between a business and a company", you should write "A business (dhandha) focuses on day-to-day operations and survival, while a company has systems, processes, and a growth mindset that allows it to scale beyond the founder's direct involvement."
    
    NEVER use vague statements like "The speaker explained..." without providing the EXACT explanation. Each point should contain SPECIFIC INFORMATION that was shared in the video, not just mention that something was discussed.
    
    DO NOT summarize or condense information - instead, break down every minute of the video into detailed points. The goal is to make watching the video completely unnecessary after reading these points. Users should feel 100% confident they haven't missed ANYTHING from the original video. This is CRITICAL - we guarantee users they don't need to watch the video if they read all points, so your thoroughness and accuracy directly impact our credibility.
    
    If you receive a transcript from Whisper AI, use EVERY PART of that transcript to create your points. Don't leave out any information from the transcript. Convert the entire transcript into well-formatted, coherent points that capture ALL the information.
    
    IMPORTANT: You MUST write the content in the language specified by the "outputLanguage" parameter:
    
    - If "English": Write in standard English
    - If "Hindi": Write in Hindi using Devanagari script
    - If "Hinglish": Write in Hindi using English script (Roman Hindi). Example: "Is video mein business coach aur consultant Shakil Kadva guidance de rahe hain."
    - If "Brazilian": Write in Brazilian Portuguese
    - If "Brazinglish": Write in Brazilian Portuguese mixed with English terms
    - If "Marathi": Write in Marathi using Devanagari script
    - If "Marathinglish": Write in Marathi using English script (Roman Marathi)
    
    This is CRITICAL - users expect content in their selected language. Do NOT default to English.
    
    2. Key Insights: A list of 5-10 essential takeaways from the video.
    
    3. Mind Map: A hierarchical representation of the video's content, with the main topic as the root node and subtopics as children.
    
    4. FAQs: 5-8 frequently asked questions and their answers based on the video content.
    
    5. MCQs: 5 multiple-choice questions with 4 options each, the correct answer index (0-3), and an explanation.
    
    6. Flash Cards: 5-10 flash cards with a concept on the front and explanation on the back.
    
    7. Study Notes: Organized notes suitable for studying the topic.
    
    8. Terminology: A list of important terms and their definitions from the video.
    
    Format your response as a simple JSON object with the following structure:
    {
      "fullCapsule": "1. Point one\n2. Point two\n3. Point three\n...",
      "keyInsights": ["Insight 1", "Insight 2", "Insight 3", ...],
      "mindMap": { "name": "Main Topic", "children": [{"name": "Subtopic 1"}, {"name": "Subtopic 2"}] },
      "faqs": [{ "question": "Question 1?", "answer": "Answer 1" }, { "question": "Question 2?", "answer": "Answer 2" }],
      "mcqs": [{ "question": "Question?", "options": ["Option 1", "Option 2", "Option 3", "Option 4"], "correctAnswer": 0, "explanation": "Explanation" }],
      "flashCards": [{ "front": "Term", "back": "Definition" }],
      "studyNotes": "Brief study notes",
      "terminology": [{ "term": "Term", "definition": "Definition", "context": "Context" }]
    }
    
    IMPORTANT: Make sure your JSON is valid and properly formatted. Use double quotes for all strings and property names.
    
    CRITICAL FORMATTING RULES:
    1. Avoid using special characters, control characters, or escape sequences in your JSON strings.
    2. Use simple plain text without formatting for all content.
    3. For the fullCapsule, use simple numbered points with plain text descriptions.
    4. Keep your JSON structure as simple as possible.
    5. Make sure all quotes, braces, and brackets are properly balanced.
    6. Do not use newlines within JSON string values - use \\n instead if needed.
    
    Example of good fullCapsule format:
    "fullCapsule": "1. First point about the video.\n2. Second point about the video.\n3. Third point about the video."
  `;
  
  // Add category-specific instructions using enum
  switch (category) {
    case ContentCategory.Educational:
      basePrompt += `
        This is an educational video. Focus on explaining concepts clearly and organizing information in a way that facilitates learning.
        For the MCQs, create questions that test understanding rather than just recall.
        For the Mind Map, organize concepts hierarchically to show relationships between ideas.
      `;
      break;
    case ContentCategory.News:
      basePrompt += `
        This is a news video. Focus on the key facts, events, people, and implications.
        Maintain objectivity and present multiple perspectives if they exist in the video.
        For the Mind Map, organize information chronologically or by topic.
      `;
      break;
    case ContentCategory.DoglaMeter:
      basePrompt += `
        This is a fact-checking analysis. Your primary goal is to identify claims made in the video and assess their accuracy.
        Add a "factCheck" section to your JSON with the following structure:
        "factCheck": {
          "claimAccuracy": number (0-100 representing percentage of accurate claims),
          "claims": [
            { "claim": "string", "assessment": "string", "evidence": "string", "manipulationTechnique": "string" (if applicable) }
          ],
          "biasAnalysis": "string"
        }
        
        Be thorough in your analysis and provide evidence for your assessments.
      `;
      break;
    case ContentCategory.Sports:
      basePrompt += `
        This is a sports video. Focus on key plays, statistics, player performances, and strategic analysis.
        Use appropriate sports terminology and explain technical concepts.
        For the Mind Map, organize by game periods, key moments, or strategic elements.
      `;
      break;
    case ContentCategory.Podcast:
      basePrompt += `
        This is a podcast episode. Focus on the conversation flow, key points made by each speaker, and the overall narrative.
        Identify different perspectives presented and note areas of agreement or disagreement.
        For the Mind Map, organize by topics discussed or by speaker contributions.
      `;
      break;
    default:
      // General category
      basePrompt += `
        Focus on capturing the main points and organizing the information in a clear and concise manner.
      `;
  }
  
  return basePrompt;
};

/**
 * Handles chat with the AI about the video content
 * @param messages Previous chat messages
 * @param transcript Video transcript
 * @param metadata Video metadata
 * @returns Promise with AI response
 */
export const chatWithAI = async (
  messages: ChatMessage[],
  transcript: VideoTranscript,
  metadata: VideoMetadata
): Promise<string> => {
  try {
    // Always use real API, never use mock data
    const useRealApi = true;
    
    if (useRealApi) {
      return withRetry(async () => {
        try {
          // Create system message with context
          const transcriptText = transcript.items.map(item => item.text).join(' ');
          const systemMessage: ChatMessage = {
            role: 'system',
            content: `
              You are an AI assistant helping with questions about a YouTube video.
              
              Video Title: ${metadata.title}
              Channel: ${metadata.channelTitle}
              
              Here's the transcript of the video:
              ${transcriptText}
              
              Answer questions based on the video content. If the answer isn't in the video, say so politely.
            `
          };
          
          // Combine system message with user messages to create a single system prompt
          const combinedSystemPrompt = `${systemMessage.content}\n\nChat History:\n${messages.map(m => `${m.role}: ${m.content}`).join('\n')}`;
          
          // Get the last user message as the user prompt
          const lastUserMessage = messages.filter(m => m.role === 'user').pop()?.content || '';
          
          // Use the multiModelService to generate content
          return await generateWithAI(combinedSystemPrompt, lastUserMessage);
        } catch (error) {
          console.error('Error chatting with AI:', error);
          throw error;
        }
      });
    } else {
      // Use mock data for development
      console.log(`Generating mock chat response for: ${metadata.title}`);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get the user's question
      const userQuestion = messages.find(m => m.role === 'user')?.content || '';
      
      // Generate a mock response based on the question
      let response = '';
      
      if (userQuestion.toLowerCase().includes('what is this video about')) {
        response = `This video is about ${metadata.title}. It covers important concepts and provides practical examples of their application. The presenter explains the fundamental principles of the topic, focusing on key aspects that are essential for understanding the subject matter.`;
      } else if (userQuestion.toLowerCase().includes('who is the presenter')) {
        response = `The presenter of this video is the creator of the content. Based on the information available, this video was published on the channel ${metadata.channelTitle}.`;
      } else if (userQuestion.toLowerCase().includes('how long is the video')) {
        response = `The video is ${metadata.duration} in length.`;
      } else if (userQuestion.toLowerCase().includes('what are the key points')) {
        response = `The key points of this video include:
1. The fundamental principles of the topic
2. Practical applications of these principles
3. Common misconceptions and their clarifications
4. Examples demonstrating how to apply the concepts in real-world scenarios
5. Essential aspects for understanding the subject matter`;
      } else if (userQuestion.toLowerCase().includes('summary')) {
        response = `Here's a summary of the video:

The video "${metadata.title}" provides a comprehensive overview of the topic, covering fundamental principles, practical applications, and common misconceptions. The presenter explains the core concepts that form the foundation of the subject matter, focusing on key aspects that are essential for understanding. 

Several real-world applications are demonstrated, showing how the concepts can be applied in various scenarios. The presenter also addresses common misconceptions, providing accurate information to correct misunderstandings.

The video takes a balanced approach, covering both theoretical principles and practical applications, making it accessible and useful for viewers.`;
      } else {
        response = `Based on the video content, I can tell you that "${metadata.title}" covers this topic in detail. The video explains fundamental principles and demonstrates practical applications. If you have more specific questions about certain aspects of the video, feel free to ask!`;
      }
      
      return response;
    }
  } catch (error) {
    console.error('Error chatting with AI:', error);
    return "I'm sorry, I encountered an error while processing your question. Please try again with a different question.";
  }
};

/**
 * Checks the token count of a text to ensure it doesn't exceed OpenAI's limits
 * @param text Text to check
 * @returns Approximate token count
 */
export const estimateTokenCount = (text: string): number => {
  // A very rough estimate: 1 token ~= 4 characters for English text
  return Math.ceil(text.length / 4);
};

/**
 * Truncates text to fit within a token limit
 * @param text Text to truncate
 * @param maxTokens Maximum number of tokens
 * @returns Truncated text
 */
export const truncateToTokenLimit = (text: string, maxTokens: number = 4000): string => {
  const estimatedTokens = estimateTokenCount(text);
  
  if (estimatedTokens <= maxTokens) {
    return text;
  }
  
  // Truncate to approximately the right length
  const ratio = maxTokens / estimatedTokens;
  const newLength = Math.floor(text.length * ratio);
  
  // Try to truncate at a sentence boundary
  const truncated = text.substring(0, newLength);
  const lastPeriod = truncated.lastIndexOf('.');
  
  if (lastPeriod > 0 && lastPeriod > newLength * 0.8) {
    return truncated.substring(0, lastPeriod + 1) + ' [truncated]';
  }
  
  return truncated + ' [truncated]';
};
